import {
  AppBar,
  Button,
  Box,
  Menu,
  MenuItem,
  Link,
  List,
  ListItem,
  useScrollTrigger,
} from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";

import useLanguage from "../../hooks/useLanguage";
import ASLIcon from "../icons/ASLIcon";
import CallIcon from "../icons/CallIcon";
import CheckmarkIcon from "../icons/CheckmarkIcon";
import DropDownIcon from "../icons/DropdownIcon";
import TextIcon from "../icons/TextIcon";
import LanguageIcon from "../icons/LanguageIcon";
import {
  replaceMainSiteLinkHostname,
  getModalityFromHostname,
} from "../../services/Utils";
import ChatDialogueIcon from "../icons/ChatDialogueIcon";
import NineEightEightSuicideCrisisLifelineLogoEN from "../icons/NineEightEightSuicideCrisisLifelineLogoEN";
import NineEightEightSuicideCrisisLifelineLogoES from "../icons/NineEightEightSuicideCrisisLifelineLogoES";

const navLinkStyles = (theme, { iconSize = { xs: 20, sm: 22 } } = {}) => ({
  position: "relative",
  ":focus::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    border: `1px dashed ${theme.palette.tertiary.main}`,
    pointerEvents: "none",
  },
  ":focus": {
    color: theme.palette.secondary.dark,
  },
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  minHeight: "24px",
  textDecoration: "none",
  gap: "6px",
  padding: "0 6px",
  fontSize: { xs: "14px", sm: "15px" },
  fontWeight: 700,
  lineHeight: "1",
  outline: "none",
  "& svg": {
    width: iconSize,
    height: iconSize,
  },
});

export default function Header() {
  const { t, language } = useLanguage();
  const modality = getModalityFromHostname(window?.location?.hostname);
  const VITE_SMS_NUMBER = import.meta.env.VITE_SMS_NUMBER || "988";
  const VITE_CALL_NUMBER = import.meta.env.VITE_CALL_NUMBER || "988";

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return (
    <AppBar
      position="sticky"
      sx={(theme) => ({
        bgcolor: theme.palette.secondary.light,
        boxShadow: "none",
        borderBottom: trigger ? "1px solid #0c172e4d" : "none",
      })}
    >
      <nav>
        <List
          sx={{
            m: 0,
            p: 0,
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            columnGap: { xs: "1.8rem", sm: "2.25rem" },
            minHeight: trigger ? 34 : 38,
            rowGap: "0",
            transition: "min-height 180ms ease",
          }}
        >
          <ListItem
            disablePadding
            sx={{
              width: "initial",
              height: trigger ? "34px" : "38px",
              margin: 0,
              transition: "height 180ms ease",
            }}
          >
            <Link
              href={`tel:${VITE_CALL_NUMBER}`}
              variant="top-nav"
              aria-label={t("navigationMenu.call")}
              sx={(theme) =>
                navLinkStyles(theme, { iconSize: { xs: 23, sm: 25 } })
              }
            >
              <CallIcon />
              {t("navigationMenu.call")}
            </Link>
          </ListItem>
          {modality === "web" && (
            <ListItem
              disablePadding
              sx={{
                width: "initial",
                height: trigger ? "34px" : "38px",
                margin: 0,
                transition: "height 180ms ease",
              }}
            >
              <Link
                href={`sms:${VITE_SMS_NUMBER}`}
                variant="top-nav"
                aria-label={t("navigationMenu.text")}
                sx={(theme) => navLinkStyles(theme)}
              >
                <TextIcon />
                {t("navigationMenu.text")}
              </Link>
            </ListItem>
          )}
          {modality === "sms" && (
            <ListItem
              disablePadding
              sx={{
                width: "initial",
                height: trigger ? "34px" : "38px",
                margin: 0,
                transition: "height 180ms ease",
              }}
            >
              <Link
                href={
                  import.meta.env.VITE_CHAT_HOST ||
                  "https://chat.988lifeline.org"
                }
                variant="top-nav"
                aria-label={t("navigationMenu.chat")}
                sx={(theme) => navLinkStyles(theme)}
              >
                <ChatDialogueIcon sx={{ pb: 0.25 }} />
                {t("navigationMenu.chat")}
              </Link>
            </ListItem>
          )}

          {language !== "es" && (
            <ListItem
              disablePadding
              sx={{
                width: "initial",
                height: trigger ? "34px" : "38px",
                margin: 0,
                transition: "height 180ms ease",
              }}
            >
              <Link
                variant="top-nav"
                href={replaceMainSiteLinkHostname(t("navigationMenu.aslLink"))}
                target="_blank"
                rel="noopener"
                aria-label={t("navigationMenu.aslAriaLabel")}
                sx={(theme) => navLinkStyles(theme)}
              >
                <ASLIcon />
                {t("navigationMenu.aslNow")}
              </Link>
            </ListItem>
          )}
        </List>
      </nav>
      <SubNav compact={trigger} />
    </AppBar>
  );
}

function SubNav({ compact = false }) {
  const { t, language } = useLanguage();

  return (
    <nav>
      <List
        sx={(theme) => ({
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: theme.palette.white,
          py: compact ? 1 : 1.4,
          px: { xs: 2.75, tiny: 3, sm: 5.8 },
          transition: "padding 180ms ease",
          [theme.breakpoints.up("sm")]: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            py: compact ? 1.3 : 1.8,
          },
          [theme.breakpoints.up("md")]: {
            py: compact ? 1.4 : 2,
          },
        })}
      >
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: "initial",
            display: "none",
            [theme.breakpoints.up("sm")]: {
              display: "block",
            },
          })}
        >
          <Link
            variant="top-nav"
            href={replaceMainSiteLinkHostname(t("navigationMenu.home.url"))}
            target="_blank"
            rel="noopener"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              textDecoration: "underline",
              textTransform: "none",
              fontFamily: "Public Sans",
              fontSize: "1rem",
              fontWeight: "bold",
              display: "inline",
              padding: 0,
              ":focus": {
                outline: `1px dashed ${theme.palette.tertiary.main}`,
                color: theme.palette.secondary.dark,
                outlineOffset: 4,
              },
            })}
          >
            {t("navigationMenu.home.label")}
          </Link>
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: "initial",
            [theme.breakpoints.up("sm")]: {
              placeSelf: "center",
            },
          })}
        >
          <Link
            href={replaceMainSiteLinkHostname(t("navigationMenu.home.url"))}
            target="_blank"
            rel="noopener"
            sx={(theme) => ({
              color: theme.palette.secondary.main,
              display: "inline-flex",
              ":hover": {
                color: theme.palette.primary.main,
              },
              ":focus": {
                outline: `1px dashed ${theme.palette.tertiary.main}`,
                outlineOffset: 4,
              },
            })}
          >
            {language.startsWith("en") ? (
              <NineEightEightSuicideCrisisLifelineLogoEN
                fill="inherit"
                sx={(theme) => ({
                  width: "100%",
                  height: "auto",
                  maxHeight: "38px",
                  [theme.breakpoints.up("tiny")]: {
                    width: "auto",
                    height: "38px",
                  },
                })}
              />
            ) : (
              <NineEightEightSuicideCrisisLifelineLogoES
                sx={{ fill: "primary.contrast", width: "auto", height: "38px" }}
              />
            )}
          </Link>
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: "initial",
            [theme.breakpoints.up("sm")]: {
              placeSelf: "center end",
            },
          })}
        >
          <LanguageSelector />
        </ListItem>
      </List>
    </nav>
  );
}

SubNav.propTypes = {
  compact: PropTypes.bool,
};

function LanguageSelector() {
  const { t, language, setLanguage } = useLanguage();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = (newLanguage) => {
    if (newLanguage && newLanguage !== language) {
      setLanguage(newLanguage);
    }
    setAnchorEl(null);
  };

  const languageLabels = {
    en: t("navigationMenu.englishLabel"),
    "en-US": t("navigationMenu.englishLabel"),
    es: t("navigationMenu.spanishLabel"),
  };

  const uniqueLanguages = {
    en: t("navigationMenu.englishLabel"),
    es: t("navigationMenu.spanishLabel"),
  };

  const isCurrentLanguage = (lang) =>
    language === lang || (language.startsWith("en") && lang === "en");

  return (
    <>
      <Button
        onClick={handleLanguageClick}
        startIcon={<LanguageIcon />}
        endIcon={<DropDownIcon sx={{ ml: -1.5, mr: 1 }} />}
        disableRipple={true}
        sx={(theme) => ({
          textTransform: "none",
          color: theme.palette.primary.contrast,
          borderRadius: "999px",
          padding: "3px 10px",
          border: `1px solid ${theme.palette.primary.contrast}`,
          fontSize: ".85rem",
          width: "120px",
          ":focus": {
            outline: `1px dashed ${theme.palette.tertiary.main}`,
            outlineOffset: 4,
          },
        })}
      >
        {languageLabels[language] || languageLabels["en"]}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleLanguageClose()}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {Object.entries(uniqueLanguages).map(([lang, label]) => (
          <MenuItem
            sx={{
              height: 20,
              width: 110,
              display: "flex",
              alignItems: "center",
            }}
            key={lang}
            onClick={() => handleLanguageClose(lang)}
            disableRipple={true}
          >
            {isCurrentLanguage(lang) ? (
              <>
                <CheckmarkIcon fontSize="medium" />
                <Box sx={{ width: "5px" }} />
              </>
            ) : (
              <Box sx={{ width: "20px" }} />
            )}
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
